import { useMemo, useRef } from 'react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { CallRecordEventType } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import {
  CallRecord,
  CallRecording,
  CallRecordTagNotification,
  CallRecordUpdateEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import { PhoneEventsOrg } from '@wetags/phone-events';
import { InfiniteData, QueryClient, useQueryClient } from 'react-query';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { TagsV2 } from '@frontend/api-tag';
import { useCallInsightController } from '@frontend/call-insight-panel';
import { RecentCallRecords } from '@frontend/phone-panels';
import { useAppScopeStore } from '@frontend/scope';
import { useVoiceAIRCustomizationFlag } from '@frontend/shared';
import {
  AllCallsTable,
  CallRecordsFiltersType,
  usePhonePageSettingShallowStore,
} from '../../hooks/use-phone-config-state-store';
import { queryKeys } from '../../query-keys';
import { formatEndDateWithTimeZone, formatStartDateWithTimeZone } from '../../utils';
import {
  applyCallRecordUpdate,
  applyCallIntelInsightUpdate,
  applyCallRecordingUpdate,
  applyFollowupTaskCountUpdate,
  applyRecentCallTagsUpdate,
  prependRecentCallRecord,
} from './call-record-cache.helpers';
import { getRecentCallsSearchQuery } from './utils';

type PendingCallRecordAiMetadata = NonNullable<CallRecord['aiMetadata']>;

const CALL_INTEL_UPDATE_EVENT_TYPES = [
  CallRecordEventType.CALL_RECORD_EVENT_TYPE_CALL_INTELLIGENCE_FINISHED_PROCESSING,
  CallRecordEventType.CALL_RECORD_EVENT_TYPE_CALL_INTELLIGENCE_NEW,
];

// updates the recent call records when a new call record is added or a call recording is finished processing
export const useCallRecordEvent = () => {
  const queryClient = useQueryClient();
  const pendingCallRecordUpdatesRef = useRef(new Map<string, PendingCallRecordAiMetadata>());
  const inFlightNewCallRecordsRef = useRef(new Set<string>());
  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  const { config: phonePageConfig } = usePhonePageSettingShallowStore('config');
  const { updateCallInsights } = useCallInsightController({ source: 'callHistory' });
  const { locationFlagValueMap } = useVoiceAIRCustomizationFlag(selectedLocationIds);
  const [isAirEventsUiEnabled] = PhoneEventsOrg.useWetag('fe-air-events-ui');

  const pageSize = phonePageConfig[AllCallsTable.RecentCalls].pageSize;
  const filters = phonePageConfig[AllCallsTable.RecentCalls].filters;

  const { data: mappedTags = {} } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      select: (data) => {
        return data?.tags.reduce<Record<string, Tag>>((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});
      },
    },
  });

  const searchQuery = useMemo(
    () => getRecentCallsSearchQuery(filters, pageSize, mappedTags),
    [filters, pageSize, mappedTags]
  );

  const qnKeys = [selectedLocationIds, ...queryKeys.recentCallLogs(searchQuery)];

  const handleCallRecordEvent = async (event: CallRecordUpdateEvent) => {
    if (event.type == CallRecordEventType.CALL_RECORD_EVENT_TYPE_NEW && event.callRecord) {
      const channelId = event.callRecord.channelId;

      if (channelId) {
        inFlightNewCallRecordsRef.current.add(channelId);
      }

      try {
        await addCallRecordToRecentCallRecords(
          event.callRecord,
          filters,
          queryClient,
          qnKeys,
          locationFlagValueMap[event.callRecord.locationId] ?? false,
          isAirEventsUiEnabled,
          pendingCallRecordUpdatesRef.current
        );
      } finally {
        if (channelId) {
          inFlightNewCallRecordsRef.current.delete(channelId);
        }
      }
    } else if (
      event.type == CallRecordEventType.CALL_RECORD_EVENT_TYPE_RECORDING_FINISHED_PROCESSING &&
      event.callRecording
    ) {
      updateCallRecordingInRecentCallRecords(event.callRecording, queryClient, qnKeys);
    } else if (event.type === CallRecordEventType.CALL_RECORD_EVENT_TYPE_VAIR_EVENT_NEW && event.callRecord) {
      const wasUpdated = updateCallRecordInRecentCallRecords(event.callRecord, queryClient, qnKeys);

      if (!wasUpdated && inFlightNewCallRecordsRef.current.has(event.callRecord.channelId)) {
        queuePendingCallRecordAiMetadata(pendingCallRecordUpdatesRef.current, event.callRecord);
      }
    } else if (CALL_INTEL_UPDATE_EVENT_TYPES.includes(event.type) && event.callIntelInsight) {
      updateCallIntelInsightInCache(event.callIntelInsight, queryClient, qnKeys);

      if (event.callIntelInsight.channelId) {
        updateCallInsights({ ...event.callIntelInsight, id: event.callIntelInsight.channelId });
        queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey.includes('GetCallMetadata') });
      }
    } else if (event.type === CallRecordEventType.CALL_RECORD_EVENT_TYPE_FOLLOWUP_TASKS_PROCESSED && event.tasksCount) {
      updateFollowupTaskCountInCache(event.tasksCount, queryClient, qnKeys);
      queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey.includes('ListTasks') });
    }
  };

  const handleCallRecordTagEvent = (event: CallRecordTagNotification) => {
    const { channelId, tagsPayload } = event;
    const tagIds = tagsPayload.map((tag) => tag.id);
    updateCallRecordTagsInRecentCalls({
      channelId,
      tagIds,
      mappedTags,
      queryClient,
      recentCallRecordsQueryKey: qnKeys,
    });
  };

  return { handleCallRecordEvent, handleCallRecordTagEvent };
};

export async function addCallRecordToRecentCallRecords(
  callRecord: CallRecord,
  appliedFilter: CallRecordsFiltersType | undefined,
  queryClient: QueryClient,
  recentCallRecordsQueryKey: (string | string[] | undefined)[],
  hasVoiceAi: boolean,
  isAirEventsUiEnabled: boolean,
  pendingUpdates?: Map<string, PendingCallRecordAiMetadata>
) {
  if (!doesCallRecordPassFilterChecks(callRecord, appliedFilter, isAirEventsUiEnabled)) {
    // if the call record does not pass the filter checks, ignore the event
    return;
  }

  await hydrateCallRecordIntoRecentCallRecords(
    callRecord,
    appliedFilter,
    queryClient,
    recentCallRecordsQueryKey,
    hasVoiceAi,
    isAirEventsUiEnabled,
    pendingUpdates
  );
}

export function queuePendingCallRecordAiMetadata(
  pendingUpdates: Map<string, PendingCallRecordAiMetadata>,
  callRecord: CallRecord
) {
  if (!callRecord.channelId || !callRecord.aiMetadata) {
    return;
  }

  pendingUpdates.set(
    callRecord.channelId,
    mergeCallRecordAiMetadata(pendingUpdates.get(callRecord.channelId), callRecord.aiMetadata) ?? callRecord.aiMetadata
  );
}

export function consumePendingCallRecordAiMetadata(
  pendingUpdates?: Map<string, PendingCallRecordAiMetadata>,
  channelId?: string
) {
  if (!pendingUpdates || !channelId) {
    return undefined;
  }

  const pendingUpdate = pendingUpdates.get(channelId);

  if (pendingUpdate) {
    pendingUpdates.delete(channelId);
  }

  return pendingUpdate;
}

export function mergeCallRecordAiMetadata(
  baseAiMetadata?: CallRecord['aiMetadata'],
  queuedAiMetadata?: CallRecord['aiMetadata']
) {
  if (!baseAiMetadata) {
    return queuedAiMetadata;
  }

  if (!queuedAiMetadata) {
    return baseAiMetadata;
  }

  const mergedAiReceptionistEventTypes = [
    ...(baseAiMetadata.aiReceptionistEventTypes ?? []),
    ...(queuedAiMetadata.aiReceptionistEventTypes ?? []),
  ];

  return {
    ...baseAiMetadata,
    ...queuedAiMetadata,
    aiReceptionistEventTypes: Array.from(new Set(mergedAiReceptionistEventTypes)),
  };
}

export function applyQueuedAiMetadataToCallRecord<T extends { aiMetadata?: CallRecord['aiMetadata'] }>(
  baseCallRecord: T,
  queuedAiMetadata?: CallRecord['aiMetadata']
): T {
  const aiMetadata = mergeCallRecordAiMetadata(baseCallRecord.aiMetadata, queuedAiMetadata);

  if (!aiMetadata) {
    return baseCallRecord;
  }

  return {
    ...baseCallRecord,
    aiMetadata,
  };
}

async function hydrateCallRecordIntoRecentCallRecords(
  callRecord: CallRecord,
  appliedFilter: CallRecordsFiltersType | undefined,
  queryClient: QueryClient,
  recentCallRecordsQueryKey: (string | string[] | undefined)[],
  hasVoiceAi: boolean,
  isAirEventsUiEnabled: boolean,
  pendingUpdates?: Map<string, PendingCallRecordAiMetadata>
) {
  const callRecordData = await queryClient.fetchQuery({
    queryKey: [callRecord?.locationId, callRecord?.channelId],
    queryFn: () =>
      PhoneCallsApi.getCallRecord({
        callRecordId: callRecord.id,
        channelId: callRecord.channelId,
        locationId: callRecord.locationId,
        // we are passing startTime, endTime for efficient lookup of call records in the backend
        startTime: callRecord.startedAt,
        endTime: callRecord.startedAt,
        hydrationFlags: {
          includePersonInfo: true,
          includePhoneTreeData: true,
          includeUserInfo: true,
          includeDownloadCallRecordingToken: true,
          includeAiCallCreditsDetail: hasVoiceAi,
        },
      }),
  });
  if (!callRecordData) {
    return;
  }

  // Start from the hydrated backend record, then layer on newer websocket AI metadata.
  const hydratedCallRecord = callRecordData.record as CallRecord;
  const callRecordWithNewEventAiMetadata = applyQueuedAiMetadataToCallRecord(hydratedCallRecord, callRecord.aiMetadata);

  // Consume any VAIR patch that arrived while the NEW-event hydration request was in flight.
  const pendingAiMetadataUpdate = consumePendingCallRecordAiMetadata(pendingUpdates, callRecord.channelId);
  const mergedCallRecord = applyQueuedAiMetadataToCallRecord(callRecordWithNewEventAiMetadata, pendingAiMetadataUpdate);

  if (!doesCallRecordPassFilterChecks(mergedCallRecord, appliedFilter, isAirEventsUiEnabled)) {
    return;
  }

  const oldData = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;
  if (!oldData) {
    console.error(
      'call records realtime update failed. unable to fetch recent calls data from query cache ',
      recentCallRecordsQueryKey
    );
    return;
  }

  const newRecord = {
    ...mergedCallRecord,
    isRecordingBeingProcessed: callRecord.isCallRecorded,
    tags: [],
  };

  queryClient.setQueryData(recentCallRecordsQueryKey, prependRecentCallRecord(oldData, newRecord));
}

export function updateCallRecordingInRecentCallRecords(
  recordingInfo: CallRecording,
  queryClient: QueryClient,
  recentCallRecordsQueryKey: (string | string[] | undefined)[]
) {
  const callRecords = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;
  const updatedCallRecords = applyCallRecordingUpdate(callRecords, recordingInfo);

  if (!updatedCallRecords) {
    return;
  }

  queryClient.setQueryData(recentCallRecordsQueryKey, updatedCallRecords);
}

export function updateCallRecordInRecentCallRecords(
  callRecord: CallRecord,
  queryClient: QueryClient,
  recentCallRecordsQueryKey: (string | string[] | undefined)[]
): boolean {
  const callRecords = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;
  const hasMatchingRecord =
    callRecords?.pages.some((page) => page.data.some((record) => record.channelId === callRecord.channelId)) ?? false;

  if (!hasMatchingRecord) {
    return false;
  }

  const updatedCallRecords = applyCallRecordUpdate(callRecords, callRecord);

  if (!updatedCallRecords) {
    return false;
  }

  queryClient.setQueryData(recentCallRecordsQueryKey, updatedCallRecords);

  return true;
}

export function updateCallIntelInsightInCache(
  callIntelInsight: CallRecordUpdateEvent['callIntelInsight'],
  queryClient: QueryClient,
  recentCallRecordsQueryKey: (string | string[] | undefined)[]
) {
  const callRecords = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;
  const updatedCallRecords = applyCallIntelInsightUpdate(callRecords, callIntelInsight);

  if (!updatedCallRecords) {
    return;
  }

  queryClient.setQueryData(recentCallRecordsQueryKey, updatedCallRecords);
}

export function updateFollowupTaskCountInCache(
  params: CallRecordUpdateEvent['tasksCount'],
  queryClient: QueryClient,
  recentCallRecordsQueryKey: (string | string[] | undefined)[]
) {
  const callRecords = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;
  const updatedCallRecords = applyFollowupTaskCountUpdate(callRecords, params);

  if (!updatedCallRecords) return;

  queryClient.setQueryData(recentCallRecordsQueryKey, updatedCallRecords);
}

export function doesCallRecordPassFilterChecks(
  callRecord: CallRecord | undefined,
  appliedFilter: CallRecordsFiltersType | undefined,
  isAirEventsUiEnabled: boolean
): boolean {
  if (!appliedFilter) {
    // if no filter is applied, then return true
    return true;
  }
  if (!callRecord) {
    return false;
  }
  if (appliedFilter.locationIds.length != 0 && !appliedFilter.locationIds.includes(callRecord.locationId ?? '')) {
    return false;
  }
  if (appliedFilter.callDirection && appliedFilter.callDirection != callRecord.direction) {
    return false;
  }
  if (!callRecord.status || (appliedFilter.status.length != 0 && !appliedFilter.status.includes(callRecord.status))) {
    return false;
  }
  if (
    appliedFilter.startDate &&
    callRecord.startedAt &&
    new Date(callRecord.startedAt) < new Date(formatStartDateWithTimeZone(appliedFilter.startDate))
  ) {
    return false;
  }
  if (
    appliedFilter.endDate &&
    callRecord.startedAt &&
    new Date(callRecord.startedAt) > new Date(formatEndDateWithTimeZone(appliedFilter.endDate))
  ) {
    return false;
  }

  if (
    (appliedFilter.officePhoneNumbers?.length ?? 0) > 0 &&
    !appliedFilter.officePhoneNumbers?.some(
      (phoneNumber) =>
        // phone number formats can be different depending on if countryCode is present or not
        // so check the last 10 digits of the phone number
        phoneNumber.slice(-10) === callRecord.officeNumber.slice(-10)
    )
  ) {
    return false;
  }

  if (appliedFilter.phoneNumber != '' || appliedFilter.name != '') {
    // if either phone number or name filter is applied, skip the call record
    return false;
  }

  if (!isAirEventsUiEnabled && (appliedFilter.aiOutcomes?.length ?? 0) > 0) {
    const recordAiOutcome = callRecord.aiMetadata?.aiOutcome;
    if (!recordAiOutcome || !appliedFilter.aiOutcomes?.includes(recordAiOutcome)) {
      return false;
    }
  }

  if (isAirEventsUiEnabled && (appliedFilter.aiEvents?.length ?? 0) > 0) {
    const recordAiEvents = callRecord.aiMetadata?.aiReceptionistEventTypes ?? [];

    if (!appliedFilter.aiEvents?.some((eventType) => recordAiEvents.includes(eventType))) {
      return false;
    }
  }

  const hasHandledByFilter =
    appliedFilter.aiReceptionistHandled === true ||
    (appliedFilter.handledByUserIds?.length ?? 0) > 0 ||
    appliedFilter.allOfficeUsers === true;

  if (hasHandledByFilter) {
    const isAiHandled = !!callRecord.aiMetadata;
    const officeUserId = callRecord.officeUserId;

    const passesAiFilter = appliedFilter.aiReceptionistHandled === true && isAiHandled;
    const passesAllUsersFilter = appliedFilter.allOfficeUsers === true && !!officeUserId;
    const passesSpecificUserFilter =
      (appliedFilter.handledByUserIds?.length ?? 0) > 0 &&
      !!officeUserId &&
      !!appliedFilter.handledByUserIds?.includes(officeUserId);

    if (!passesAiFilter && !passesAllUsersFilter && !passesSpecificUserFilter) {
      return false;
    }
  }

  return true;
}

export const updateCallRecordTagsInRecentCalls = ({
  channelId,
  tagIds,
  mappedTags,
  queryClient,
  recentCallRecordsQueryKey,
}: {
  channelId: string;
  tagIds: string[];
  mappedTags: Record<string, Tag>;
  queryClient: QueryClient;
  recentCallRecordsQueryKey: (string | string[] | undefined)[];
}) => {
  const callRecords = queryClient.getQueryData(recentCallRecordsQueryKey) as InfiniteData<RecentCallRecords>;
  const updatedCallRecords = applyRecentCallTagsUpdate({
    callRecords,
    channelId,
    tagIds,
    mappedTags,
  });

  if (!updatedCallRecords) {
    return;
  }

  queryClient.setQueryData(recentCallRecordsQueryKey, updatedCallRecords);
};
